import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, FileStack, LayoutDashboard, ScanText, Sparkles, Wand2 } from "lucide-react";
import { PUBLIC_TOOLS, absoluteUrl } from "@/lib/tools-catalog";
import { buildToolsCollectionJsonLd } from "@/lib/seo/tool-structured-data";

export const Route = createFileRoute("/tools/")({
  head: () => {
    const title = "Free Tools — AI Humaniser, Paraphraser, OCR & PDF Toolkit | Nkadimeng Danny";
    const description =
      "Practical, no-signup tools for South African students and small businesses: AI Humaniser, Paraphraser, OCR Scanner and a browser-based PDF toolkit.";
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "website" },
        { property: "og:url", content: absoluteUrl("/tools") },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
      ],
      links: [{ rel: "canonical", href: absoluteUrl("/tools") }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(buildToolsCollectionJsonLd()),
        },
      ],
    };
  },
  component: ToolsHub,
});

const ICONS = {
  humaniser: Sparkles,
  paraphraser: Wand2,
  ocr: ScanText,
  pdf: FileStack,
} as const;

function ToolsHub() {
  return (
    <>
      <section className="border-b border-border bg-secondary/40">
        <div className="container-narrow py-16 md:py-20">
          <span className="gold-rule" />
          <p className="text-xs uppercase tracking-[0.25em] text-gold">Free tools</p>
          <h1 className="mt-2 font-display text-4xl text-navy md:text-5xl">
            Practical tools for students & small business
          </h1>
          <p className="mt-4 max-w-3xl text-base text-muted-foreground">
            A growing set of everyday utilities. AI features include a free daily allowance,
            then 1 credit per use. PDF tools run entirely on your device.
          </p>
          <Link
            to={"/portal/tools-dashboard" as any}
            className="mt-5 inline-flex items-center gap-2 rounded-md border border-navy bg-white px-4 py-2 text-sm font-semibold text-navy hover:bg-navy hover:text-white"
          >
            <LayoutDashboard className="h-4 w-4" /> View your credits & free-use dashboard
          </Link>
        </div>
      </section>

      <section className="container-narrow py-12">
        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {PUBLIC_TOOLS.map((tool) => {
            const Icon = ICONS[tool.id];
            return (
              <article
                key={tool.path}
                className="flex flex-col rounded-lg border border-input bg-card p-6"
              >
                <span className="mb-3 inline-flex h-10 w-10 items-center justify-center rounded-md bg-navy/5 text-navy">
                  <Icon className="h-5 w-5" />
                </span>
                <h2 className="font-display text-xl text-navy">{tool.name}</h2>
                <p className="mt-2 flex-1 text-sm text-muted-foreground">{tool.description}</p>
                <dl className="mt-4 space-y-1 text-xs">
                  <div className="flex justify-between gap-3">
                    <dt className="text-muted-foreground">Free</dt>
                    <dd className="text-right font-medium text-navy">{tool.freeLabel}</dd>
                  </div>
                  <div className="flex justify-between gap-3">
                    <dt className="text-muted-foreground">Paid</dt>
                    <dd className="text-right font-medium text-navy">{tool.paidLabel}</dd>
                  </div>
                </dl>
                <Link
                  to={tool.path as any}
                  className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-navy hover:underline"
                >
                  Open tool <ArrowRight className="h-4 w-4" />
                </Link>
              </article>
            );
          })}
        </div>
      </section>
    </>
  );
}